import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const PICKER_SELECTOR =
    '[data-venue-path-picker]';

function parseCoordinate(value) {
    const parsed = Number.parseFloat(
        value
    );

    return Number.isFinite(parsed)
        ? parsed
        : null;
}

function parseJsonArray(value) {
    try {
        const parsed = JSON.parse(
            value || '[]'
        );

        return Array.isArray(parsed)
            ? parsed
            : [];
    } catch {
        return [];
    }
}

function orientation(
    a,
    b,
    c
) {
    return (
        (b.longitude - a.longitude)
        * (c.latitude - a.latitude)
        - (b.latitude - a.latitude)
        * (c.longitude - a.longitude)
    );
}

function segmentIntersection(
    a,
    b,
    c,
    d
) {
    const denominator =
        (
            (a.longitude - b.longitude)
            * (c.latitude - d.latitude)
            - (a.latitude - b.latitude)
            * (c.longitude - d.longitude)
        );

    if (
        Math.abs(denominator)
        < 1e-12
    ) {
        return null;
    }

    const determinantAB =
        (
            a.longitude * b.latitude
            - a.latitude * b.longitude
        );

    const determinantCD =
        (
            c.longitude * d.latitude
            - c.latitude * d.longitude
        );

    const longitude =
        (
            determinantAB
            * (c.longitude - d.longitude)
            - (a.longitude - b.longitude)
            * determinantCD
        ) / denominator;

    const latitude =
        (
            determinantAB
            * (c.latitude - d.latitude)
            - (a.latitude - b.latitude)
            * determinantCD
        ) / denominator;

    const within = (
        value,
        start,
        end
    ) =>
        value
        >= Math.min(start, end) - 1e-10
        && value
        <= Math.max(start, end) + 1e-10;

    if (
        ! within(
            longitude,
            a.longitude,
            b.longitude
        )
        || ! within(
            latitude,
            a.latitude,
            b.latitude
        )
        || ! within(
            longitude,
            c.longitude,
            d.longitude
        )
        || ! within(
            latitude,
            c.latitude,
            d.latitude
        )
    ) {
        return null;
    }

    return {
        latitude,
        longitude,
    };
}

function parsePath(value) {
    try {
        const parsed = JSON.parse(
            value || '[]'
        );

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((point) => {
                const latitude =
                    parseCoordinate(
                        point?.latitude
                    );

                const longitude =
                    parseCoordinate(
                        point?.longitude
                    );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return null;
                }

                return {
                    latitude,
                    longitude,
                };
            })
            .filter(Boolean);
    } catch {
        return [];
    }
}

function initializePathPicker(picker) {
    if (
        picker.dataset
            .venuePathMapReady
        === 'true'
    ) {
        return;
    }

    const form = picker.closest(
        '[data-venue-path-form]'
    );

    const mapElement =
        picker.querySelector(
            '[data-venue-path-map]'
        );

    const pathInput =
        form?.querySelector(
            '[data-venue-path-points]'
        );

    const junctionInputs =
        form?.querySelector(
            '[data-venue-path-junction-inputs]'
        );

    const pointCount =
        picker.querySelector(
            '[data-venue-path-point-count]'
        );

    const status =
        picker.querySelector(
            '[data-venue-path-status]'
        );

    const undoButton =
        picker.querySelector(
            '[data-venue-path-undo]'
        );

    const clearButton =
        picker.querySelector(
            '[data-venue-path-clear]'
        );

    if (
        !form
        || !mapElement
        || !pathInput
    ) {
        return;
    }

    picker.dataset.venuePathMapReady =
        'true';

    const centerLatitude =
        parseCoordinate(
            picker.dataset
                .centerLatitude
        );

    const centerLongitude =
        parseCoordinate(
            picker.dataset
                .centerLongitude
        );

    let points = parsePath(
        pathInput.value
    );

    const existingPaths =
        parseJsonArray(
            picker.dataset.existingPaths
        );

    const existingJunctions =
        parseJsonArray(
            picker.dataset.existingJunctions
        );

    let selectedJunctionIds =
        junctionInputs
            ? Array.from(
                junctionInputs.querySelectorAll(
                    '[data-venue-path-junction-id]'
                )
            )
                .map(
                    (input) =>
                        Number.parseInt(
                            input.value,
                            10
                        )
                )
                .filter(Number.isInteger)
            : [];

    const currentPathId =
        Number.parseInt(
            picker.dataset.currentPathId
            || '',
            10
        );

    const junctionCreateUrl =
        picker.dataset.junctionCreateUrl
        || null;

    const SNAP_DISTANCE_PX = 18;

    const nearestJunctionToClick = (
        latlng
    ) => {
        const clickPoint =
            map.latLngToContainerPoint(
                latlng
            );

        let nearest = null;
        let nearestDistance =
            Number.POSITIVE_INFINITY;

        existingJunctions.forEach(
            (junction) => {
                const latitude =
                    parseCoordinate(
                        junction.latitude
                    );
                const longitude =
                    parseCoordinate(
                        junction.longitude
                    );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return;
                }

                const junctionPoint =
                    map.latLngToContainerPoint(
                        [
                            latitude,
                            longitude,
                        ]
                    );

                const distance =
                    clickPoint.distanceTo(
                        junctionPoint
                    );

                if (
                    distance
                    < nearestDistance
                ) {
                    nearestDistance =
                        distance;
                    nearest = {
                        ...junction,
                        latitude,
                        longitude,
                    };
                }
            }
        );

        return (
            nearest
            && nearestDistance
                <= SNAP_DISTANCE_PX
        )
            ? nearest
            : null;
    };

    let polyline = null;
    let pointMarkers = [];

    const map = L.map(
        mapElement,
        {
            zoomControl: true,
            scrollWheelZoom: false,
        }
    ).setView(
        [
            centerLatitude ?? 0,
            centerLongitude ?? 0,
        ],
        centerLatitude !== null
            && centerLongitude !== null
            ? 17
            : 2
    );

    L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
            maxZoom: 20,
            attribution:
                '&copy; OpenStreetMap contributors',
        }
    ).addTo(map);

    const contextLayer =
        L.layerGroup().addTo(map);

    existingPaths.forEach(
        (existingPath) => {
            if (
                Number(existingPath.id)
                === currentPathId
            ) {
                return;
            }

            const pathPoints =
                Array.isArray(
                    existingPath.path_points
                )
                    ? existingPath.path_points
                        .map((point) => {
                            const latitude =
                                parseCoordinate(
                                    point?.latitude
                                );
                            const longitude =
                                parseCoordinate(
                                    point?.longitude
                                );

                            return (
                                latitude !== null
                                && longitude !== null
                            )
                                ? [
                                    latitude,
                                    longitude,
                                ]
                                : null;
                        })
                        .filter(Boolean)
                    : [];

            if (pathPoints.length < 2) {
                return;
            }

            L.polyline(
                pathPoints,
                {
                    color: '#64748b',
                    weight: 4,
                    opacity:
                        existingPath.is_active
                            ? 0.72
                            : 0.35,
                    dashArray:
                        existingPath.is_active
                            ? null
                            : '7 7',
                }
            )
                .addTo(contextLayer)
                .bindTooltip(
                    existingPath.name
                    || 'Existing path',
                    {
                        sticky: true,
                    }
                );
        }
    );

    const junctionMarkers =
        new Map();

    existingJunctions.forEach(
        (junction) => {
            const latitude =
                parseCoordinate(
                    junction.latitude
                );
            const longitude =
                parseCoordinate(
                    junction.longitude
                );

            if (
                latitude === null
                || longitude === null
            ) {
                return;
            }

            const marker =
                L.circleMarker(
                    [
                        latitude,
                        longitude,
                    ],
                    {
                        radius: 7,
                        color: '#ffffff',
                        weight: 2,
                        fillColor: '#4f46e5',
                        fillOpacity:
                            junction.is_active
                                ? 0.95
                                : 0.45,
                    }
                )
                    .addTo(contextLayer)
                    .bindTooltip(
                        `${junction.name || 'Junction'}${
                            junction.type
                                ? ` · ${junction.type}`
                                : ''
                        }`,
                        {
                            direction: 'top',
                        }
                    );

            junctionMarkers.set(
                Number(junction.id),
                marker
            );
        }
    );

    const crossingWarningLayer =
        L.layerGroup().addTo(map);

    let crossingWarningCount = 0;

    const isNearExistingJunction = (
        point
    ) => {
        return existingJunctions.some(
            (junction) => {
                const latitude =
                    parseCoordinate(
                        junction.latitude
                    );
                const longitude =
                    parseCoordinate(
                        junction.longitude
                    );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return false;
                }

                const intersectionPoint =
                    map.latLngToContainerPoint(
                        [
                            point.latitude,
                            point.longitude,
                        ]
                    );

                const junctionPoint =
                    map.latLngToContainerPoint(
                        [
                            latitude,
                            longitude,
                        ]
                    );

                return (
                    intersectionPoint.distanceTo(
                        junctionPoint
                    ) <= 14
                );
            }
        );
    };

    const renderCrossingWarnings = () => {
        crossingWarningLayer
            .clearLayers();

        crossingWarningCount = 0;

        if (points.length < 2) {
            return;
        }

        const seen = new Set();

        for (
            let pointIndex = 0;
            pointIndex
                < points.length - 1;
            pointIndex += 1
        ) {
            const start =
                points[pointIndex];
            const end =
                points[pointIndex + 1];

            existingPaths.forEach(
                (existingPath) => {
                    if (
                        Number(existingPath.id)
                        === currentPathId
                    ) {
                        return;
                    }

                    const pathPoints =
                        Array.isArray(
                            existingPath.path_points
                        )
                            ? existingPath.path_points
                                .map((point) => {
                                    const latitude =
                                        parseCoordinate(
                                            point?.latitude
                                        );
                                    const longitude =
                                        parseCoordinate(
                                            point?.longitude
                                        );

                                    return (
                                        latitude !== null
                                        && longitude !== null
                                    )
                                        ? {
                                            latitude,
                                            longitude,
                                        }
                                        : null;
                                })
                                .filter(Boolean)
                            : [];

                    for (
                        let existingIndex = 0;
                        existingIndex
                            < pathPoints.length - 1;
                        existingIndex += 1
                    ) {
                        const intersection =
                            segmentIntersection(
                                start,
                                end,
                                pathPoints[
                                    existingIndex
                                ],
                                pathPoints[
                                    existingIndex + 1
                                ]
                            );

                        if (
                            ! intersection
                            || isNearExistingJunction(
                                intersection
                            )
                        ) {
                            continue;
                        }

                        const key =
                            `${intersection.latitude.toFixed(6)}:${intersection.longitude.toFixed(6)}`;

                        if (seen.has(key)) {
                            continue;
                        }

                        seen.add(key);
                        crossingWarningCount += 1;

                        const warningMarker =
                            L.circleMarker(
                                [
                                    intersection.latitude,
                                    intersection.longitude,
                                ],
                                {
                                    radius: 8,
                                    color: '#dc2626',
                                    weight: 3,
                                    fillColor: '#ffffff',
                                    fillOpacity: 0.95,
                                }
                            )
                                .addTo(
                                    crossingWarningLayer
                                )
                                .bindTooltip(
                                    `Path crossing without a nearby junction${
                                        existingPath.name
                                            ? ` · ${existingPath.name}`
                                            : ''
                                    }${
                                        junctionCreateUrl
                                        && Number.isFinite(
                                            currentPathId
                                        )
                                            ? ' · Click to add junction'
                                            : ' · Save this path first'
                                    }`,
                                    {
                                        direction: 'top',
                                    }
                                );

                        if (
                            junctionCreateUrl
                            && Number.isFinite(
                                currentPathId
                            )
                        ) {
                            warningMarker.on(
                                'click',
                                () => {
                                    const url =
                                        new URL(
                                            junctionCreateUrl,
                                            window.location.origin
                                        );

                                    url.searchParams.set(
                                        'latitude',
                                        intersection.latitude.toFixed(7)
                                    );

                                    url.searchParams.set(
                                        'longitude',
                                        intersection.longitude.toFixed(7)
                                    );

                                    url.searchParams.append(
                                        'path_ids[]',
                                        String(
                                            currentPathId
                                        )
                                    );

                                    url.searchParams.append(
                                        'path_ids[]',
                                        String(
                                            existingPath.id
                                        )
                                    );

                                    window.location.assign(
                                        url.toString()
                                    );
                                }
                            );
                        }
                    }
                }
            );
        }
    };

    let highlightedJunctionId = null;

    const clearSnapHighlight = () => {
        if (
            highlightedJunctionId
            === null
        ) {
            return;
        }

        const marker =
            junctionMarkers.get(
                highlightedJunctionId
            );

        const junction =
            existingJunctions.find(
                (item) =>
                    Number(item.id)
                    === highlightedJunctionId
            );

        marker?.setStyle({
            radius: 7,
            color: '#ffffff',
            weight: 2,
            fillColor: '#4f46e5',
            fillOpacity:
                junction?.is_active
                    ? 0.95
                    : 0.45,
        });

        highlightedJunctionId = null;
    };

    const highlightSnapJunction = (
        junction
    ) => {
        const junctionId =
            Number(junction?.id);

        if (
            ! Number.isFinite(junctionId)
        ) {
            clearSnapHighlight();
            return;
        }

        if (
            highlightedJunctionId
            === junctionId
        ) {
            return;
        }

        clearSnapHighlight();

        const marker =
            junctionMarkers.get(
                junctionId
            );

        marker?.setStyle({
            radius: 10,
            color: '#f59e0b',
            weight: 3,
            fillColor: '#f59e0b',
            fillOpacity: 1,
        });

        marker?.bringToFront();

        highlightedJunctionId =
            junctionId;
    };

    const syncJunctionInputs = () => {
        if (!junctionInputs) {
            return;
        }

        junctionInputs.innerHTML = '';

        [...new Set(selectedJunctionIds)]
            .forEach(
                (junctionId) => {
                    const input =
                        document.createElement(
                            'input'
                        );

                    input.type = 'hidden';
                    input.name =
                        'junction_ids[]';
                    input.value =
                        String(junctionId);
                    input.dataset
                        .venuePathJunctionId =
                        String(junctionId);

                    junctionInputs.appendChild(
                        input
                    );
                }
            );
    };

    const syncInput = () => {
        pathInput.value = JSON.stringify(
            points.map((point) => ({
                latitude: Number(
                    point.latitude
                        .toFixed(7)
                ),
                longitude: Number(
                    point.longitude
                        .toFixed(7)
                ),
            }))
        );
    };

    const updateStatus = () => {
        const count = points.length;

        if (pointCount) {
            pointCount.textContent =
                `${count} ${
                    count === 1
                        ? 'point'
                        : 'points'
                }`;
        }

        undoButton?.toggleAttribute(
            'disabled',
            count === 0
        );

        clearButton?.toggleAttribute(
            'disabled',
            count === 0
        );

        if (!status) {
            return;
        }

        if (count === 0) {
            status.textContent =
                'Click the starting point, then continue along the route.';
        } else if (count === 1) {
            status.textContent =
                'Add at least one more point to create the path.';
        } else {
            status.textContent =
                'Path is valid. Add more points to follow curves and intersections, or save it.';
        }
    };

    const clearMarkers = () => {
        pointMarkers.forEach(
            (marker) => {
                map.removeLayer(
                    marker
                );
            }
        );

        pointMarkers = [];
    };

    const renderPath = (
        fit = false
    ) => {
        if (polyline) {
            map.removeLayer(
                polyline
            );

            polyline = null;
        }

        clearMarkers();

        points.forEach(
            (point, index) => {
                const marker =
                    L.circleMarker(
                        [
                            point.latitude,
                            point.longitude,
                        ],
                        {
                            radius: 6,
                            color: '#ffffff',
                            weight: 2,
                            fillColor: '#1d4ed8',
                            fillOpacity: 1,
                        }
                    )
                        .addTo(map)
                        .bindTooltip(
                            index === 0
                                ? 'Start'
                                : `Point ${
                                    index + 1
                                }`,
                            {
                                direction: 'top',
                            }
                        );

                pointMarkers.push(
                    marker
                );
            }
        );

        if (points.length >= 2) {
            polyline = L.polyline(
                points.map((point) => [
                    point.latitude,
                    point.longitude,
                ]),
                {
                    color: '#2563eb',
                    weight: 5,
                    opacity: 0.9,
                }
            ).addTo(map);
        }

        renderCrossingWarnings();

        syncInput();
        updateStatus();

        if (
            crossingWarningCount > 0
            && status
        ) {
            status.textContent =
                `${crossingWarningCount} path ${
                    crossingWarningCount === 1
                        ? 'crossing needs'
                        : 'crossings need'
                } a nearby junction connection.`;
        }

        if (
            fit
            && points.length > 0
        ) {
            const bounds =
                L.latLngBounds(
                    points.map(
                        (point) => [
                            point.latitude,
                            point.longitude,
                        ]
                    )
                );

            if (bounds.isValid()) {
                map.fitBounds(
                    bounds,
                    {
                        padding: [50, 50],
                        maxZoom: 18,
                        animate: false,
                    }
                );
            }
        }
    };

    map.on(
        'mousemove',
        (event) => {
            const snappedJunction =
                nearestJunctionToClick(
                    event.latlng
                );

            if (snappedJunction) {
                highlightSnapJunction(
                    snappedJunction
                );
            } else {
                clearSnapHighlight();
            }
        }
    );

    map.on(
        'mouseout',
        clearSnapHighlight
    );

    map.on(
        'click',
        (event) => {
            const snappedJunction =
                nearestJunctionToClick(
                    event.latlng
                );

            points.push({
                latitude:
                    snappedJunction
                        ? snappedJunction.latitude
                        : event.latlng.lat,
                longitude:
                    snappedJunction
                        ? snappedJunction.longitude
                        : event.latlng.lng,
                junctionId:
                    snappedJunction
                        ? Number(snappedJunction.id)
                        : null,
            });

            if (snappedJunction) {
                selectedJunctionIds.push(
                    Number(snappedJunction.id)
                );
                selectedJunctionIds = [
                    ...new Set(
                        selectedJunctionIds
                    ),
                ];
                syncJunctionInputs();
            }

            renderPath();

            if (snappedJunction) {
                highlightSnapJunction(
                    snappedJunction
                );

                if (status) {
                    status.textContent =
                        `Snapped to junction: ${
                            snappedJunction.name
                            || 'Junction'
                        }.`;
                }
            } else {
                clearSnapHighlight();
            }
        }
    );

    undoButton?.addEventListener(
        'click',
        () => {
            if (
                points.length === 0
            ) {
                return;
            }

            const removedPoint =
                points.pop();

            if (removedPoint?.junctionId) {
                const stillUsed =
                    points.some(
                        (point) =>
                            Number(point.junctionId)
                            === Number(
                                removedPoint.junctionId
                            )
                    );

                if (!stillUsed) {
                    selectedJunctionIds =
                        selectedJunctionIds
                            .filter(
                                (junctionId) =>
                                    junctionId
                                    !== Number(
                                        removedPoint.junctionId
                                    )
                            );
                    syncJunctionInputs();
                }
            }

            renderPath();
        }
    );

    clearButton?.addEventListener(
        'click',
        () => {
            points = [];
            selectedJunctionIds = [];
            syncJunctionInputs();
            renderPath();
        }
    );

    form.addEventListener(
        'submit',
        (event) => {
            if (
                points.length >= 2
            ) {
                syncInput();
                return;
            }

            event.preventDefault();

            if (status) {
                status.textContent =
                    'Draw at least two points before saving this path.';
            }

            mapElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    );

    syncJunctionInputs();

    renderPath(
        points.length > 0
    );

    window.setTimeout(
        () =>
            map.invalidateSize(),
        100
    );
}

function initializePathPickers() {
    document
        .querySelectorAll(
            PICKER_SELECTOR
        )
        .forEach(
            initializePathPicker
        );
}

if (
    document.readyState
    === 'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        initializePathPickers,
        {
            once: true,
        }
    );
} else {
    initializePathPickers();
}

document.addEventListener(
    'livewire:navigated',
    initializePathPickers
);
